/**
 * ClientPerformanceRollup — Iter Feb 2026 R42 · Phase 3.
 *
 * Per-client roll-up: aggregate KPIs across all brands under the client,
 * top-10 campaigns, and lead-form breakdown. One-click branded PDF.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { API as API_BASE } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Download, Users, FileText } from "lucide-react";

const fmtMoney = (v) => typeof v === "number" ? `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—";
const fmtInt = (v) => typeof v === "number" ? v.toLocaleString("en-IN") : "—";
const fmtPct = (v) => typeof v === "number" ? `${v.toFixed(2)}%` : "—";

export default function ClientPerformanceRollup() {
  const { activeWorkspaceId } = useAuth();
  const { cid } = useParams();
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!activeWorkspaceId || !cid) return;
    try {
      const { data: d } = await api.get(`/workspaces/${activeWorkspaceId}/perf/clients/${cid}/rollup`, {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setData(d);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load rollup")); }
  }, [activeWorkspaceId, cid, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = () => {
    const token = localStorage.getItem("co_token");
    fetch(`${API_BASE}/workspaces/${activeWorkspaceId}/perf/clients/${cid}/pdf-report?date_from=${dateFrom}&date_to=${dateTo}`,
        { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `${data?.client?.name || "client"}-${dateFrom}_${dateTo}.pdf`;
        a.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("PDF failed"));
  };

  if (!data) return <div className="p-6 text-sm text-muted-foreground">Loading rollup…</div>;
  const t = data.totals;

  return (
    <div className="flex flex-col h-full" data-testid="client-perf-rollup-page">
      <PageHeader
        eyebrow="Performance · Client"
        title={data.client.name}
        description={`Roll-up across ${data.brands.length} brand${data.brands.length === 1 ? "" : "s"} · ${dateFrom} → ${dateTo}`}
        actions={
          <div className="flex items-center gap-2">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 rounded-none text-xs w-36" />
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 rounded-none text-xs w-36" />
            <Link to="/app/performance/multi-brand">
              <Button variant="outline" className="rounded-none h-9"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
            </Link>
            <Button className="rounded-none h-9" onClick={downloadPdf} data-testid="client-rollup-pdf-btn">
              <Download className="w-4 h-4 mr-1" /> Download PDF
            </Button>
          </div>
        }
      />
      <div className="flex-1 overflow-auto px-4 sm:px-6 md:px-10 py-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {[
            ["Spend", fmtMoney(t.spend)],
            ["Impr", fmtInt(t.impressions)],
            ["Clicks", fmtInt(t.clicks)],
            ["CTR", fmtPct(t.ctr)],
            ["Leads", fmtInt(t.leads)],
            ["ROAS", t.roas ? `${t.roas}×` : "—"],
          ].map(([l, v]) => (
            <div key={l} className="border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
              <div className="text-lg font-semibold font-mono">{v}</div>
            </div>
          ))}
        </div>

        <div className="border">
          <div className="px-3 py-2 border-b bg-neutral-50 text-[10px] uppercase tracking-widest text-muted-foreground">
            Brands under client · {data.brands.length}
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-neutral-50">
              <tr className="border-b">
                <th className="text-left px-2 py-1.5">Brand</th>
                <th className="text-right px-2 py-1.5">Spend</th>
                <th className="text-right px-2 py-1.5">Leads</th>
                <th className="text-right px-2 py-1.5">CPL</th>
                <th className="text-right px-2 py-1.5">Revenue</th>
                <th className="text-right px-2 py-1.5">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {data.brands.map(b => (
                <tr key={b.brand_id} className="border-b hover:bg-neutral-50">
                  <td className="px-2 py-1.5 font-medium">{b.brand_name}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(b.spend)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmtInt(b.leads)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(b.cpl)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(b.revenue)}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-semibold">{b.roas ? `${b.roas}×` : "—"}</td>
                </tr>
              ))}
              {data.brands.length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">No brands linked to this client yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border">
            <div className="px-3 py-2 border-b bg-neutral-50 text-[10px] uppercase tracking-widest text-muted-foreground">Top campaigns</div>
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr className="border-b">
                  <th className="text-left px-2 py-1.5">Campaign</th>
                  <th className="text-right px-2 py-1.5">Spend</th>
                  <th className="text-right px-2 py-1.5">CTR</th>
                </tr>
              </thead>
              <tbody>
                {data.top_campaigns.slice(0, 10).map(c => (
                  <tr key={c.id} className="border-b">
                    <td className="px-2 py-1.5 truncate max-w-[220px]" title={c.name}>
                      <Link to={`/app/performance/campaigns/${c.id}`} className="hover:underline">{c.name}</Link>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(c.spend)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtPct(c.ctr)}</td>
                  </tr>
                ))}
                {data.top_campaigns.length === 0 && <tr><td colSpan={3} className="py-3 text-center text-muted-foreground">No campaigns.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="border">
            <div className="px-3 py-2 border-b bg-neutral-50 text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
              <FileText className="w-3 h-3" /> Lead forms breakdown
            </div>
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr className="border-b">
                  <th className="text-left px-2 py-1.5">Form</th>
                  <th className="text-right px-2 py-1.5">Leads</th>
                </tr>
              </thead>
              <tbody>
                {data.lead_forms_breakdown.map(f => (
                  <tr key={f.form_id} className="border-b">
                    <td className="px-2 py-1.5 truncate max-w-[220px]">{f.name}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtInt(f.leads)}</td>
                  </tr>
                ))}
                {data.lead_forms_breakdown.length === 0 && <tr><td colSpan={2} className="py-3 text-center text-muted-foreground">No lead forms yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
